import { ConfidenceSlider } from '@/components/ConfidenceSlider'
import { InfoTooltip } from '@/components/InfoTooltip'
import { NumericField } from '@/components/NumericField'
import { OptionalSettingSelect } from '@/components/OptionalSettingSelect'
import { fromPercent, toPercent } from '@/lib/confidence'
import { numberField, parseOptionalNumber } from '@/lib/numberField'
import { ON_EXHAUSTED_LABELS, RETRY_TRIGGER_LABELS } from '@/lib/pageHintRetry'
import { rangeError } from '@/lib/settingRanges'
import type {
  AcceptanceDefaults,
  ChunkingSettings,
  DocumentTypeEntry,
  GlobalChunking,
  GlobalPageHintRetry,
  PageHintRetrySettings,
} from '@/types/bundle'

export interface GeneralTabProps {
  entry: DocumentTypeEntry
  onChange: (entry: DocumentTypeEntry) => void
  globalDefaults: AcceptanceDefaults
  /** global_defaults.page_hint_retry - what applies when this type sets nothing. */
  globalPageHintRetry: GlobalPageHintRetry
  /** global_defaults.chunking - what applies when this type sets nothing. */
  globalChunking: GlobalChunking
}

// What a blank override falls back to: the platform value when Global
// Configs (Page Hints, Chunking) sets one, else the engine's built-in
// default (never repeated here, so it can't drift from the backend).
function inheritedLabel(value: string | number | undefined, labels?: Record<string, string>): string {
  if (value === undefined) return 'Built-in default'
  return `Platform default (${labels ? labels[value] : value})`
}

export function GeneralTab({ entry, onChange, globalDefaults, globalPageHintRetry, globalChunking }: GeneralTabProps) {
  const pageHintRetry = entry.defaults?.page_hint_retry ?? {}
  const chunking = entry.defaults?.chunking ?? {}

  const minConfidenceDefault = entry.defaults?.acceptance?.min_confidence_default ?? globalDefaults.min_confidence_default
  const criticalMinConfidenceDefault =
    entry.defaults?.acceptance?.critical_min_confidence_default ?? globalDefaults.critical_min_confidence_default

  function updateAcceptanceDefaults(patch: Partial<AcceptanceDefaults>) {
    onChange({
      ...entry,
      defaults: { ...entry.defaults, acceptance: { ...entry.defaults?.acceptance, ...patch } },
    })
  }

  function updateChunking(patch: Partial<ChunkingSettings>) {
    onChange({ ...entry, defaults: { ...entry.defaults, chunking: { ...chunking, ...patch } } })
  }

  function updatePageHintRetry(patch: Partial<PageHintRetrySettings>) {
    onChange({ ...entry, defaults: { ...entry.defaults, page_hint_retry: { ...pageHintRetry, ...patch } } })
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">Echo original documents</h2>
        <label className="flex w-fit items-center gap-2 text-body text-text-primary">
          <input
            type="checkbox"
            checked={entry.echo_original_documents ?? false}
            onChange={(event) => onChange({ ...entry, echo_original_documents: event.target.checked })}
          />
          Echo original documents
          <InfoTooltip text="When enabled, the original uploaded document may be included in the response for this document type. This alone doesn't turn echoing on - the product type this document belongs to, and a platform-wide output setting, must also allow it." />
        </label>
        <p className="text-meta text-text-secondary">
          Requires the same setting to also be enabled on this document type's product type and in platform-wide
          output settings - neither is configurable in this app yet.
        </p>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Acceptance defaults
          <InfoTooltip text="These become the default minimum confidence for every field on this document type. A field can still set its own override on the Fields tab, which always wins over this." />
        </h2>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Default min confidence
            <InfoTooltip text="The minimum confidence score a field on this document type needs before its value is accepted, unless the field sets its own override. Falls back to the platform-wide default (tick mark) when not set here." />
          </span>
          <ConfidenceSlider
            value={toPercent(minConfidenceDefault)}
            referenceValue={toPercent(globalDefaults.min_confidence_default)}
            onChange={(percent) => updateAcceptanceDefaults({ min_confidence_default: fromPercent(percent) })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
            Default min confidence (critical fields)
            <InfoTooltip text="Same as above, but for fields marked Critical on the Fields tab - critical fields need a higher confidence score before they're accepted." />
          </span>
          <ConfidenceSlider
            value={toPercent(criticalMinConfidenceDefault)}
            referenceValue={toPercent(globalDefaults.critical_min_confidence_default)}
            onChange={(percent) => updateAcceptanceDefaults({ critical_min_confidence_default: fromPercent(percent) })}
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Page hint retry (this document type)
          <InfoTooltip text="Overrides the platform-wide page hint retry settings (Global Configs > Page Hints) for this document type only. Only used when every field has a page hint. Leave a setting blank to use the platform value." />
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <NumericField
            id="phr-override-max-widen-radius"
            label="Max widen radius (pages)"
            tooltip="How many extra rounds are tried, each looking one more page either side of a missing field's hint. 0 turns widening off."
            min={0}
            placeholder={inheritedLabel(globalPageHintRetry.max_widen_radius_default)}
            value={numberField(pageHintRetry.max_widen_radius_default)}
            error={rangeError('max_widen_radius_default', pageHintRetry.max_widen_radius_default)}
            onChange={(raw) => updatePageHintRetry({ max_widen_radius_default: parseOptionalNumber(raw) })}
          />
          <NumericField
            id="phr-override-whole-document-threshold"
            label="Whole-document threshold (%)"
            tooltip="When the pages to send would cover at least this much of the document, the whole document is sent instead."
            min={0}
            max={100}
            placeholder={inheritedLabel(globalPageHintRetry.whole_document_threshold_percent_default)}
            value={numberField(pageHintRetry.whole_document_threshold_percent_default)}
            error={rangeError(
              'whole_document_threshold_percent_default',
              pageHintRetry.whole_document_threshold_percent_default,
            )}
            onChange={(raw) => updatePageHintRetry({ whole_document_threshold_percent_default: parseOptionalNumber(raw) })}
          />
          <OptionalSettingSelect
            label="Retry trigger"
            tooltip="Which missing fields start a retry for this document type."
            value={pageHintRetry.retry_trigger_default}
            labels={RETRY_TRIGGER_LABELS}
            unsetLabel={inheritedLabel(globalPageHintRetry.retry_trigger_default, RETRY_TRIGGER_LABELS)}
            onChange={(value) =>
              updatePageHintRetry({ retry_trigger_default: value as PageHintRetrySettings['retry_trigger_default'] })
            }
          />
          <OptionalSettingSelect
            label="When retries run out"
            tooltip="What happens if a field is still missing after the last widening round."
            value={pageHintRetry.on_exhausted_default}
            labels={ON_EXHAUSTED_LABELS}
            unsetLabel={inheritedLabel(globalPageHintRetry.on_exhausted_default, ON_EXHAUSTED_LABELS)}
            onChange={(value) =>
              updatePageHintRetry({ on_exhausted_default: value as PageHintRetrySettings['on_exhausted_default'] })
            }
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Chunking (this document type)
          <InfoTooltip text="Overrides the platform-wide target chunk size (Global Configs > Chunking) for this document type only. How many chunks run at once is platform-wide only. Leave blank to use the platform value." />
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <NumericField
            id="general-chunking-target-pages"
            label="Target pages per chunk"
            tooltip="The most pages in one model call for this document type; never more than the extractor's max pages. Smaller chunks run faster in parallel but cost more input tokens."
            min={1}
            step="1"
            placeholder={inheritedLabel(globalChunking.target_pages_per_chunk_default)}
            value={numberField(chunking.target_pages_per_chunk_default)}
            error={rangeError('target_pages_per_chunk_default', chunking.target_pages_per_chunk_default)}
            onChange={(raw) => updateChunking({ target_pages_per_chunk_default: parseOptionalNumber(raw) })}
          />
        </div>
      </section>
    </div>
  )
}
